import { useEffect } from 'react';
import { useLocation } from 'react-router';
import hotkeys from 'hotkeys-js';

interface GlobalShortcutsOptions {
  // Orders page specific handlers
  onReload?: () => void;
  onFind?: () => void;
  onNewOrder?: () => void;
  
  // Create/Edit page specific handlers
  onCreate?: () => void;
  onUpdate?: () => void;
  onOrdersList?: () => void;
  
  // Templates drawer handlers
  onTemplatesTab?: () => void;
  onOrdersTab?: () => void;
  
  // Common handlers
  onCancel?: () => void;
  onShowShortcuts?: () => void;
  
  /** Set to false if you DON'T want Alt+ hotkeys in a normal browser tab */
  enableBrowserFallback?: boolean;
}

// Zoom utility functions
const ZOOM_STORAGE_KEY = 'app-zoom-level';

const getZoomContainer = (): HTMLElement => {
  // Target the main content area more specifically
  const mainContent = document.querySelector('.MuiBox-root') || 
                     document.querySelector('[role="main"]') ||
                     document.querySelector('#root') || 
                     document.body;
  return mainContent as HTMLElement;
};

const getCurrentZoom = (): number => {
  try {
    const container = getZoomContainer();
    
    // Method 1: Use zoom property (Chrome/Edge)
    const zoomValue = (container.style as any).zoom;
    if (zoomValue) {
      return parseFloat(zoomValue);
    }
    
    // Method 2: Use CSS transform scale
    const transform = container.style.transform;
    if (transform && transform.includes('scale(')) {
      const match = transform.match(/scale\(([^)]+)\)/);
      if (match) {
        return parseFloat(match[1]);
      }
    }
    
    return 1;
  } catch (e) {
    return 1;
  }
};

const setZoom = (zoomLevel: number): void => {
  try {
    const container = getZoomContainer();
    
    // Method 1: Use zoom property (Chrome/Edge)
    (container.style as any).zoom = zoomLevel.toString();
  } catch (e) {
    try {
      // Method 2: Use CSS transform scale with proper containment
      const container = getZoomContainer();
      container.style.transform = `scale(${zoomLevel})`;
      container.style.transformOrigin = 'top left';
      container.style.overflow = 'hidden'; // Prevent overflow
    } catch (e2) {
      // Method 3: Fallback to body zoom (less ideal)
      console.warn('Container zoom not supported, falling back to body zoom');
      (document.body.style as any).zoom = zoomLevel.toString();
    }
  }
};

const saveZoomLevel = (zoomLevel: number): void => {
  try {
    localStorage.setItem(ZOOM_STORAGE_KEY, zoomLevel.toString());
  } catch (e) {
    console.warn('[Zoom] Could not save zoom level to localStorage:', e);
  }
  
  // Also communicate to desktop shell if available
  try {
    const isShell = !!(window as any).chrome?.webview;
    if (isShell) {
      // Send zoom level to desktop shell for persistence
      (window as any).chrome.webview.postMessage({
        action: 'saveZoomLevel',
        zoomLevel: zoomLevel
      });
    }
  } catch (e) {
    console.warn('[Zoom] Could not send zoom level to desktop shell:', e);
  }
};

const loadZoomLevel = (): number => {
  try {
    const saved = localStorage.getItem(ZOOM_STORAGE_KEY);
    if (saved) {
      const zoomLevel = parseFloat(saved);
      return isNaN(zoomLevel) ? 1 : Math.max(0.1, Math.min(3, zoomLevel)); // Clamp between 0.1 and 3
    }
  } catch (e) {
    console.warn('[Zoom] Could not load zoom level from localStorage:', e);
  }
  
  // Try to get zoom level from desktop shell
  try {
    const isShell = !!(window as any).chrome?.webview;
    if (isShell) {
      // Request zoom level from desktop shell
      (window as any).chrome.webview.postMessage({
        action: 'getZoomLevel'
      });
    }
  } catch (e) {
    console.warn('[Zoom] Could not request zoom level from desktop shell:', e);
  }
  
  return 1;
};

// Restore zoom immediately when script loads
const restoreZoomOnLoad = () => {
  const savedZoom = loadZoomLevel();
  if (savedZoom !== 1) {
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      setZoom(savedZoom);
    }, 0);
  }
};

// Call immediately
restoreZoomOnLoad();

/**
 * Unified global keyboard shortcuts handler that's aware of the current page.
 *
 * • Inside the WPF shell: receives JSON messages from WebView2  
 * • In a regular tab: binds shortcuts via hotkeys-js
 * • Routes actions based on current page context
 */
export function useGlobalShortcuts({
  onReload,
  onFind,
  onNewOrder,
  onCreate,
  onUpdate,
  onOrdersList,
  onCancel,
  onShowShortcuts,
  onTemplatesTab,
  onOrdersTab,
  enableBrowserFallback = true,
}: GlobalShortcutsOptions) {
  const location = useLocation();
  
  // Determine current page context
  const isOrdersPage = location.pathname === '/orders';
  const isCreatePage = location.pathname === '/order-create';
  const isEditPage = location.pathname === '/order-edit';
  const isTemplatePage = location.pathname === '/template';

  // Restore zoom level on mount (additional safety)
  useEffect(() => {
    const savedZoom = loadZoomLevel();
    if (savedZoom !== 1) {
      setZoom(savedZoom);
    }
  }, []);

  useEffect(() => {
    const isShell = !!(window as any).chrome?.webview;

    /* ──────────────────── 1. DESKTOP SHELL ──────────────────── */
    if (isShell) {
      const handler = (ev: any) => {
        const { action } =
          typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data ?? {};
        
        switch (action) {
          // Global actions (work on all pages)
          case 'cancel':
            onCancel?.();
            break;
          case 'shortcuts':
            onShowShortcuts?.();
            break;
          case 'zoom_in':
            const currentZoomIn = getCurrentZoom();
            const newZoomIn = Math.min(3, currentZoomIn + 0.1); // Max 300%
            setZoom(newZoomIn);
            saveZoomLevel(newZoomIn);
            break;
          case 'zoom_out':
            const currentZoomOut = getCurrentZoom();
            const newZoomOut = Math.max(0.1, currentZoomOut - 0.1); // Min 10%
            setZoom(newZoomOut);
            saveZoomLevel(newZoomOut);
            break;
          case 'zoom_reset':
            setZoom(1);
            saveZoomLevel(1);
            break;
          case 'zoomLevel':
            // Response from desktop shell with saved zoom level
            const { zoomLevel: shellZoomLevel } = ev.data;
            if (shellZoomLevel && shellZoomLevel !== 1) {
              setZoom(shellZoomLevel);
            }
            break;
          
          // Orders page specific actions
          case 'reload':
            // Always perform hard reload (Ctrl+Shift+R equivalent) to bypass cache
            window.location.reload();
            break;
          case 'find':
            if (isOrdersPage) {
              onFind?.();
            }
            break;
          case 'new':
            if (isOrdersPage) {
              onNewOrder?.();
            } else {
              onCreate?.();
            }
            break;
          case 'list':
            if (isOrdersPage) {
              // Navigate to orders list (Ctrl+L) - could be used for other list navigation
            } else {
              onOrdersList?.();
            }
            break;
          
          // Create/Edit page specific actions
          case 'create':
            if (isCreatePage || isEditPage || isTemplatePage) {
              onCreate?.();
            }
            break;
          case 'update':
            if (isCreatePage || isEditPage || isTemplatePage) {
              onUpdate?.();
            }
            break;
          
          // Templates drawer actions
          case 'templates_tab':
            if (isCreatePage || isEditPage || isTemplatePage) {
              onTemplatesTab?.();
            }
            break;
          case 'orders_tab':
            if (isCreatePage || isEditPage || isTemplatePage) {
              onOrdersTab?.();
            }
            break;
          
          default:
            break;
        }
      };

      (window as any).chrome.webview.addEventListener('message', handler);
      return () =>
        (window as any).chrome.webview.removeEventListener('message', handler);
    }

    /* ──────────────── 2. NORMAL BROWSER TAB (fallback) ──────────────── */
    if (enableBrowserFallback) {
      /* Reload, Find, and New Order shortcuts */
      hotkeys('ctrl+r, ctrl+f, ctrl+n', (e, hk) => {
        e.preventDefault();
        switch (hk.key) {
          case 'ctrl+r':
            // Always perform hard reload (Ctrl+Shift+R equivalent) to bypass cache
            window.location.reload();
            break;
          case 'ctrl+f':
            if (isOrdersPage) {
              onFind?.();
            }
            break;
          case 'ctrl+n':
            if (isOrdersPage) {
              onNewOrder?.();
            } else {
              onCreate?.();
            }
            break;
        }
      });

      /* Templates drawer shortcuts */
      hotkeys('ctrl+t, ctrl+o', (e, hk) => {
        e.preventDefault();
        switch (hk.key) {
          case 'ctrl+t':
            if (isCreatePage || isEditPage || isTemplatePage) {
              onTemplatesTab?.();
            }
            break;
          case 'ctrl+o':
            if (isCreatePage || isEditPage || isTemplatePage) {
              onOrdersTab?.();
            }
            break;
        }
      });

      /* Zoom shortcuts including reset */
      hotkeys('ctrl+0', (e, hk) => {
        e.preventDefault();
        setZoom(1);
        saveZoomLevel(1);
      });

      /* Try different zoom shortcut patterns */
      hotkeys('ctrl+plus, ctrl+minus, ctrl+equal, ctrl+shift+equal, ctrl+=, ctrl+-', (e, hk) => {
        e.preventDefault();
        const currentZoom = getCurrentZoom();
        
        if (hk.key.includes('plus') || hk.key.includes('equal') || hk.key.includes('=')) {
          const newZoom = Math.min(3, currentZoom + 0.1);
          setZoom(newZoom);
          saveZoomLevel(newZoom);
        } else if (hk.key.includes('minus') || hk.key.includes('-')) {
          const newZoom = Math.max(0.1, currentZoom - 0.1);
          setZoom(newZoom);
          saveZoomLevel(newZoom);
        }
      });

      /* Show shortcuts */
      hotkeys('ctrl+/, ctrl+shift+/, ctrl+question, ctrl+shift+question, ctrl+191', (e, hk) => {
        e.preventDefault();
        onShowShortcuts?.();
      });

      // Fallback keyboard event listener for Ctrl+?
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.ctrlKey && event.key === '?') {
          event.preventDefault();
          onShowShortcuts?.();
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        hotkeys.unbind();
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [onReload, onFind, onNewOrder, onCreate, onUpdate, onOrdersList, onCancel, onShowShortcuts, onTemplatesTab, onOrdersTab, enableBrowserFallback, location.pathname]);
} 